import { ampli } from '@/ampli';
import Link from 'next/link';
import { useRef } from 'react';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { CheckCircleFilledIcon } from '@/ui/Icons/CheckCircleFilledIcon';
import { SolidCopyIcon } from '@/ui/Icons/SolidCopyIcon';

import { buildTokenPageRoute } from '..';
import { NETWORK_NAME_PER_CHAIN_ID, solana } from '../../../../constants/chain';
import { fontStyles } from '../../../../styles/typography';
import { formatShortAddress } from '../../../../utils/address';
import { classNames } from '../../../../utils/classnames';
import { DiscoveryTokenResult } from '../../../../utils/discovery/types';
import { isTokenAddressNativeAsset } from '../../../../utils/multichain';
import { truncate } from '../../../../utils/string';

type TokenNameCellProps = {
  item: DiscoveryTokenResult;
  showTickerFirst?: boolean;
};

export function TokenNameCell({ item, showTickerFirst }: TokenNameCellProps) {
  const displayTimer = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!item || !item.token) {
    return null;
  }

  const isNativeToken = isTokenAddressNativeAsset(item.token.address, item.token.chainId);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (displayTimer.current) {
      clearTimeout(displayTimer.current);
    }

    ampli.tokenAddressCopied({
      token_address: item.token.address,
      token_chain_id: item.token.chainId,
      token_chain_name: NETWORK_NAME_PER_CHAIN_ID[item.token.chainId] || '',
    });
    navigator.clipboard.writeText(item.token.address);
    displayTimer.current = setTimeout(() => {
      displayTimer.current = null;
      if (buttonRef.current) {
        buttonRef.current.dataset.copied = 'false';
      }
    }, 2000);
    if (buttonRef.current) {
      buttonRef.current.dataset.copied = 'true';
    }
  };

  return (
    <Link
      className={classNames('flex items-center gap-4', showTickerFirst ? 'min-w-0' : undefined)}
      href={buildTokenPageRoute(item.token)}
    >
      <TokenAvatar
        chainId={item.token.chainId}
        address={item.token.address}
        launchpad={item.launchpad}
      />
      <div className={classNames('flex flex-col', showTickerFirst ? 'min-w-0' : undefined)}>
        <span
          className={fontStyles({
            variant: 'LABEL_MD',
            class: [
              'leading-4',
              'overflow-hidden',
              'text-ellipsis',
              'whitespace-nowrap',
              ...(showTickerFirst
                ? ['block', 'max-w-[130px]', 'tablet:max-w-full']
                : ['max-w-[130px]', 'tablet:max-w-[200px]']),
            ],
          })}
        >
          {showTickerFirst
            ? item.token.symbol
            : item.token.name.length > 24
              ? truncate(item.token.name, 24, true)
              : item.token.name}
        </span>
        <div
          className={classNames(
            'relative overflow-hidden h-5 z-10',
            showTickerFirst ? 'w-32 tablet:w-52' : 'w-32',
          )}
        >
          {isNativeToken ? (
            <span
              className={fontStyles({
                variant: 'BODY_SM',
                class: [
                  'text-token-gray-800',
                  'leading-[18px]',
                  'block',
                  'overflow-hidden',
                  'text-ellipsis',
                  'whitespace-nowrap',
                  ...(showTickerFirst
                    ? ['max-w-[128px]', 'tablet:max-w-[208px]']
                    : ['max-w-[128px]']),
                ],
              })}
            >
              {showTickerFirst
                ? item.token.name.length > 20
                  ? truncate(item.token.name, 20, true)
                  : item.token.name
                : item.token.symbol}
            </span>
          ) : (
            <div
              className={classNames(
                'transition-transform',
                'duration-200',
                'absolute',
                'top-0',
                'left-0',
                '-translate-y-5.5',
                'group-hover:translate-y-0',
                'ease-out',
                'flex',
                'flex-col',
                'gap-1',
              )}
            >
              <span
                className={fontStyles({
                  variant: 'BODY_SM',
                  class: [
                    'text-token-gray-800',
                    'leading-[18px]',
                    'opacity-0',
                    'group-hover:opacity-100',
                    'transition-opacity',
                    'duration-200',
                    'ease-out',
                    'flex',
                    'items-center',
                  ],
                })}
              >
                {formatShortAddress(
                  item.token.address,
                  item.token.chainId === solana.id ? 4 : 6,
                  4,
                )}
                <button
                  ref={buttonRef}
                  data-copied={false}
                  onClick={handleCopy}
                  className="z-50 text-token-gray-600 hover:text-token-gray-700 transition-colors ease-out duration-150 relative h-4 w-4 group"
                >
                  <SolidCopyIcon
                    className="group-data-[copied=true]:opacity-0 opacity-100 absolute transition-opacity top-0 left-0 duration-200 ease-out"
                    height={16}
                    width={16}
                  />
                  <CheckCircleFilledIcon className="group-data-[copied=true]:opacity-100 opacity-0 absolute transition-opacity top-0 left-0 duration-200 ease-out" />
                </button>
              </span>
              <button
                className={fontStyles({
                  variant: 'BODY_SM',
                  class: [
                    'text-token-gray-800',
                    'leading-[18px]',
                    'opacity-100',
                    'group-hover:opacity-0',
                    'transition-opacity',
                    'duration-200',
                    'ease-out',
                    'block',
                    'overflow-hidden',
                    'text-ellipsis',
                    'whitespace-nowrap',
                    ...(showTickerFirst
                      ? ['max-w-[128px]', 'tablet:max-w-[208px]']
                      : ['max-w-[128px]']),
                  ],
                })}
              >
                {showTickerFirst
                  ? item.token.name.length > 20
                    ? truncate(item.token.name, 20, true)
                    : item.token.name
                  : item.token.symbol}
              </button>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
